'use client';

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useWorkflowStore } from '../../../stores/workflowStore';
import { ColumnSelector } from '../ColumnSelector';
import { useNodeInputColumns } from '../../../hooks/useNodeInputs';
import { useNodeSchema } from '../../../contexts/SchemaContext';
import { getNodeDisplayLabel } from '../../../utils/nodeDisplayLabel';

export type BarcodeType = 'QR_CODE' | 'CODE128' | 'EAN13';
export type BarcodeFormat = 'image' | 'svg' | 'base64';

export interface BarcodeConfigParams {
  barcodeType?: BarcodeType;
  sourceField?: string;
  outputField?: string;
  format?: BarcodeFormat;
}

const BARCODE_TYPES: { value: BarcodeType; labelKey: string }[] = [
  { value: 'QR_CODE', labelKey: 'nodes.config.barcode.typeQRCode' },
  { value: 'CODE128', labelKey: 'nodes.config.barcode.typeCode128' },
  { value: 'EAN13', labelKey: 'nodes.config.barcode.typeEan13' },
];

const FORMATS: { value: BarcodeFormat; labelKey: string }[] = [
  { value: 'svg', labelKey: 'nodes.config.barcode.formatSvg' },
  { value: 'image', labelKey: 'nodes.config.barcode.formatImage' },
  { value: 'base64', labelKey: 'nodes.config.barcode.formatBase64' },
];

interface BarcodeConfigProps {
  nodeId: string;
  config: BarcodeConfigParams;
}

export function BarcodeConfig({ nodeId, config }: BarcodeConfigProps) {
  const { t, i18n } = useTranslation();
  const { nodes, updateNodeConfig } = useWorkflowStore();
  const { register } = useNodeSchema(nodeId);
  const [barcodeType, setBarcodeType] = useState<BarcodeType>(
    config.barcodeType ?? (config as BarcodeConfigParams & { barcode_type?: BarcodeType }).barcode_type ?? 'QR_CODE'
  );
  const [sourceField, setSourceField] = useState(
    config.sourceField ?? (config as BarcodeConfigParams & { source_field?: string }).source_field ?? ''
  );
  const [outputField, setOutputField] = useState(
    config.outputField ?? (config as BarcodeConfigParams & { output_field?: string }).output_field ?? 'barcode'
  );
  const [format, setFormat] = useState<BarcodeFormat>(config.format ?? 'svg');
  const inputColumns = useNodeInputColumns(nodeId);

  // 当 config 变化时（如 AI 生成工作流后），同步更新内部状态
  useEffect(() => {
    const cfg = config as BarcodeConfigParams & { barcode_type?: BarcodeType; source_field?: string; output_field?: string };
    if (cfg.barcodeType || cfg.barcode_type) {
      setBarcodeType(cfg.barcodeType ?? cfg.barcode_type ?? 'QR_CODE');
    }
    if (cfg.sourceField || cfg.source_field) {
      setSourceField(cfg.sourceField ?? cfg.source_field ?? '');
    }
    if (cfg.outputField || cfg.output_field) {
      setOutputField(cfg.outputField ?? cfg.output_field ?? 'barcode');
    }
    if (cfg.format) {
      setFormat(cfg.format ?? 'svg');
    }
  }, [config]);

  // 注册条码节点输出 schema（上游列 + 新增条码列），供删除列等下游节点显示可选列
  useEffect(() => {
    const node = nodes.find(n => n.id === nodeId);
    if (!node || !outputField.trim()) return;
    const upstream = (inputColumns ?? []).map(c => c.column);
    const hasBarcodeCol = upstream.some(c => c.name === outputField.trim());
    const outputColumns = hasBarcodeCol
      ? upstream
      : [...upstream, { name: outputField.trim(), type: 'string' as const, nullable: true, index: upstream.length }];
    if (outputColumns.length === 0) return;
    register({
      nodeType: node.type,
      nodeName: getNodeDisplayLabel(node.type, node.data?.label as string | undefined, t, i18n),
      columns: outputColumns,
    });
  }, [nodeId, nodes, inputColumns, outputField, register, t, i18n]);

  const save = (updates: Partial<BarcodeConfigParams>) => {
    updateNodeConfig(nodeId, {
      type: 'barcode',
      params: {
        barcodeType,
        sourceField,
        outputField,
        format,
        ...updates,
      },
    });
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="text-xs font-medium text-zinc-500">
          {t('nodes.config.barcode.barcodeType')}
        </label>
        <select
          value={barcodeType}
          onChange={(e) => {
            const v = e.target.value as BarcodeType;
            setBarcodeType(v);
            save({ barcodeType: v });
          }}
          className="w-full px-3 py-2 text-sm bg-white border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 text-zinc-900"
        >
          {BARCODE_TYPES.map(({ value, labelKey }) => (
            <option key={value} value={value}>
              {t(labelKey)}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-2">
        <label className="text-xs font-medium text-zinc-500">
          {t('nodes.config.barcode.sourceField')}
        </label>
        <ColumnSelector
          value={sourceField}
          onChange={(col) => {
            setSourceField(col);
            save({ sourceField: col });
          }}
          placeholder={
            inputColumns?.length
              ? t('nodes.config.barcode.selectSourceField')
              : t('nodes.config.filter.connectInputFirst')
          }
          columns={inputColumns ?? []}
        />
      </div>

      <div className="space-y-2">
        <label className="text-xs font-medium text-zinc-500">
          {t('nodes.config.barcode.outputField')}
        </label>
        <input
          type="text"
          value={outputField}
          onChange={(e) => setOutputField(e.target.value)}
          onBlur={() => save({ outputField: outputField })}
          placeholder={t('nodes.config.barcode.outputFieldPlaceholder')}
          className="w-full px-3 py-2 text-sm bg-white border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 text-zinc-900 placeholder:text-zinc-400"
        />
      </div>

      <div className="space-y-2">
        <label className="text-xs font-medium text-zinc-500">
          {t('nodes.config.barcode.outputFormat')}
        </label>
        <select
          value={format}
          onChange={(e) => {
            const v = e.target.value as BarcodeFormat;
            setFormat(v);
            save({ format: v });
          }}
          className="w-full px-3 py-2 text-sm bg-white border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 text-zinc-900"
        >
          {FORMATS.map(({ value, labelKey }) => (
            <option key={value} value={value}>
              {t(labelKey)}
            </option>
          ))}
        </select>
        <p className="text-[10px] text-zinc-400">{t('nodes.config.barcode.formatHint')}</p>
      </div>
    </div>
  );
}
